import type { FallbackRender } from '@sentry/react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AlertTriangle,
  InfoIcon,
  RefreshCwIcon,
  StepForwardIcon,
} from 'lucide-react'

import { useRundownSnap } from '@/features/rundown/rundown-page.store'

import { ErrorViewer } from '@/components/util/ErrorViewer'
import { userStore } from '@/features/user/user.store'

export const RundownErrorBoundary: FallbackRender = ({ error, resetError }) => {
  const { id: rundownId } = useRundownSnap()

  const typedError =
    error instanceof Error
      ? error
      : new Error(
          typeof error === 'string'
            ? error
            : 'An unknown runtime error occurred',
        )

  return (
    <div className="min-w-max h-full w-full p-8 bg-background overflow-scroll">
      <Card className="w-full max-w-2xl m-4 mx-auto shadow-2xl">
        <CardHeader className="flex flex-row items-center gap-4">
          <div className="p-3 bg-destructive/10 text-destructive rounded-lg">
            <AlertTriangle />
          </div>
          <div className="py-2">
            <CardTitle>Error</CardTitle>
            <CardDescription>An unexpected error occurred.</CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <Alert className="p-4 bg-muted/50 border-white/40">
            <InfoIcon />
            <AlertTitle>What now?</AlertTitle>
            <AlertDescription>
              <p>
                If this is the first time, click <b>Continue</b> and pick up
                where you left off.
              </p>
              <p>
                If this is a recurring error, click <b>Refresh</b> to load the
                latest version of Rundown Studio.
              </p>
              <p>
                If this is a persistent issue, please report it by clicking{' '}
                <b>Copy log</b> and pasting the contents into an email for us at{' '}
                <a href="mailto:hello@rundownstudio.app">
                  hello@rundownstudio.app
                </a>
              </p>
            </AlertDescription>
          </Alert>
          <div className="text-muted-foreground"></div>
          <ErrorViewer
            error={typedError}
            meta={{
              'Rundown ID': rundownId,
              'User ID': userStore.userId,
            }}
          />
        </CardContent>

        <CardFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => window.location.reload()}>
            <RefreshCwIcon />
            Refresh
          </Button>

          <Button variant="outline" onClick={resetError}>
            <StepForwardIcon />
            Continue
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
